import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { APP_DEEP_LINK_DESTINATIONS, buildNativeDeepLink, resolveAppDestinationFromWebUrl } from '@aluvo/constants/src/deepLinks';
import { ANALYTICS_EVENTS, trackEvent } from '../../analytics';
import { APP_CONFIG, detectPlatform, getAppCtaHref, isMobileBrowser } from '../../config/appConfig';
import styles from './StickyMobileCTA.module.css';

const DISMISS_KEY = 'aluvo_open_app_banner_dismissed_at';
const SESSION_KEY = 'aluvo_open_app_banner_seen';
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000;
const FALLBACK_DELAY_MS = 1500;

function readTimestamp(key) {
    try {
        const value = window.localStorage.getItem(key);
        return value ? Number(value) : null;
    } catch {
        return null;
    }
}

function writeTimestamp(key) {
    try {
        window.localStorage.setItem(key, String(Date.now()));
    } catch {}
}

function readSessionFlag() {
    try {
        return window.sessionStorage.getItem(SESSION_KEY) === 'true';
    } catch {
        return false;
    }
}

function writeSessionFlag() {
    try {
        window.sessionStorage.setItem(SESSION_KEY, 'true');
    } catch {}
}

function shouldSuppressForDismissal(now = Date.now()) {
    const dismissedAt = readTimestamp(DISMISS_KEY);
    return !!dismissedAt && now - dismissedAt < DISMISS_DURATION_MS;
}

function getCurrentDestination(location) {
    const url = `${APP_CONFIG.webUrl}${location.pathname}${location.search || ''}`;
    const destination = resolveAppDestinationFromWebUrl(url);
    // The Top Movies creator is web-first for guests: no "open in app" banner over the editor.
    if (destination?.type === APP_DEEP_LINK_DESTINATIONS.TOP_MOVIES) return null;
    return destination;
}

export function shouldShowOpenAppBanner({ location, mobile = isMobileBrowser(), sessionSeen = readSessionFlag(), dismissed = shouldSuppressForDismissal() }) {
    if (!mobile || sessionSeen || dismissed) return false;
    return !!getCurrentDestination(location);
}

function StickyMobileCTA() {
    const location = useLocation();
    const [visible, setVisible] = useState(false);
    const [dismissed, setDismissed] = useState(() => shouldSuppressForDismissal());
    const sessionSeenRef = useRef(readSessionFlag());
    const viewedRef = useRef(null);
    const fallbackTimerRef = useRef(null);

    const destination = useMemo(() => getCurrentDestination(location), [location]);
    const platform = detectPlatform();
    const storeHref = getAppCtaHref();
    const nativeHref = destination ? buildNativeDeepLink(destination.type, destination.params) : null;

    useEffect(() => {
        if (!shouldShowOpenAppBanner({ location, sessionSeen: sessionSeenRef.current, dismissed })) {
            setVisible(false);
            return;
        }

        setVisible(true);
        writeSessionFlag();
        sessionSeenRef.current = true;

        const viewKey = `${destination.type}:${destination.nativePath}`;
        if (viewedRef.current !== viewKey) {
            viewedRef.current = viewKey;
            trackEvent(ANALYTICS_EVENTS.APP_OPEN_BANNER_VIEWED, {
                destination_type: destination.type,
                platform,
            }, { onceKey: viewKey });
        }
    }, [destination, dismissed, location, platform]);

    useEffect(() => () => {
        if (fallbackTimerRef.current) window.clearTimeout(fallbackTimerRef.current);
    }, []);

    function close(reason) {
        setVisible(false);
        setDismissed(true);
        writeTimestamp(DISMISS_KEY);
        trackEvent(ANALYTICS_EVENTS.APP_OPEN_BANNER_DISMISSED, {
            destination_type: destination?.type,
            platform,
            reason,
        });
    }

    function handleOpen() {
        if (!nativeHref || !destination) return;

        trackEvent(ANALYTICS_EVENTS.APP_OPEN_BANNER_OPEN_CLICKED, {
            destination_type: destination.type,
            platform,
        });
        trackEvent(ANALYTICS_EVENTS.APP_OPEN_ATTEMPTED, {
            destination_type: destination.type,
            platform,
        });

        window.location.href = nativeHref;

        if (storeHref) {
            fallbackTimerRef.current = window.setTimeout(() => {
                if (document.visibilityState === 'hidden') return;
                trackEvent(ANALYTICS_EVENTS.APP_OPEN_STORE_FALLBACK, {
                    destination_type: destination.type,
                    platform,
                });
                window.open(storeHref, '_blank', 'noopener,noreferrer');
            }, FALLBACK_DELAY_MS);
        }
    }

    if (!visible || !destination || !nativeHref) return null;

    return (
        <div className={styles.bar} role="complementary" aria-label="Open this page in Aluvo">
            <div className={styles.mark} aria-hidden="true">A</div>
            <div className={styles.textCol}>
                <span className={styles.bannerTitle}>Open in Aluvo</span>
                <span className={styles.bannerSub}>Get the full experience in the app.</span>
            </div>

            <button className={styles.secondary} onClick={() => close('stay_on_web')}>
                Stay on Web
            </button>
            <button className={styles.cta} onClick={handleOpen}>
                Open App
            </button>

            <button className={styles.dismiss} onClick={() => close('dismiss')} aria-label="Dismiss open in app banner">
                x
            </button>
        </div>
    );
}

export default StickyMobileCTA;
